import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Instagram, Facebook, Mail } from "lucide-react";
import Logo from "@/components/Logo";

const WA_SVG = (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const Footer = () => (
  <footer data-testid="footer" className="bg-[#2E4E72] text-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">

        {/* Brand */}
        <div className="lg:col-span-1">
          <Logo iconHeight={44} showText />
          <p className="mt-4 text-slate-300 text-sm leading-relaxed">
            Jornadas excepcionais com serviço extraordinário. Especialistas em transformar seus sonhos de viagem em realidade.
          </p>
          <div className="flex gap-4 mt-6">
            <a href="https://www.instagram.com/mahloviagens/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram"
              className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#89A6A3] transition-colors duration-300">
              <Instagram size={16} />
            </a>
            <a href="https://www.facebook.com/mahloviagens" target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook"
              className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#89A6A3] transition-colors duration-300">
              <Facebook size={16} />
            </a>
            <a href="https://wa.me/5513996995877" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" aria-label="WhatsApp"
              className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-green-500 transition-colors duration-300">
              {WA_SVG}
            </a>
          </div>
        </div>

        {/* Navigation */}
        <div>
          <h4 className="font-bold text-white text-sm tracking-widest uppercase mb-5">Navegação</h4>
          <ul className="space-y-3">
            {[["Início","/"],["Sobre Nós","/sobre"],["Serviços","/servicos"],["Destinos","/destinos"],["Depoimentos","/depoimentos"],["Contato","/contato"]].map(([label, href]) => (
              <li key={href}>
                <Link to={href} data-testid={`footer-link-${label.toLowerCase()}`}
                  className="text-slate-300 text-sm hover:text-[#89A6A3] transition-colors duration-200">{label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Services */}
        <div>
          <h4 className="font-bold text-white text-sm tracking-widest uppercase mb-5">Serviços</h4>
          <ul className="space-y-3">
            {["Planejamento Personalizado","Pacotes de Férias","Viagens de Luxo","Viagens em Grupo","Consultoria de Viagem","Lua de Mel"].map((s) => (
              <li key={s}>
                <Link to="/servicos" className="text-slate-300 text-sm hover:text-[#89A6A3] transition-colors duration-200">{s}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-bold text-white text-sm tracking-widest uppercase mb-5">Contato</h4>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <Phone size={16} className="text-[#89A6A3] mt-0.5 flex-shrink-0" />
              <a href="tel:+5513996995877" data-testid="footer-phone" className="text-slate-300 text-sm hover:text-[#89A6A3] transition-colors">(13) 99699-5877</a>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={16} className="text-[#89A6A3] mt-0.5 flex-shrink-0" />
              <a href="mailto:contato@mahloviagens.com.br" data-testid="footer-email" className="text-slate-300 text-sm hover:text-[#89A6A3] transition-colors">contato@mahloviagens.com.br</a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={16} className="text-[#89A6A3] mt-0.5 flex-shrink-0" />
              <span className="text-slate-300 text-sm">R. Othon Feliciano, 02 - Cj. 43<br/>Santos - SP</span>
            </li>
            <li className="flex items-start gap-3">
              <Clock size={16} className="text-[#89A6A3] mt-0.5 flex-shrink-0" />
              <span className="text-slate-300 text-sm">Todos os dias até 18:00</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
        <p className="text-slate-400 text-sm">© {new Date().getFullYear()} Mahlo Viagens. Todos os direitos reservados.</p>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-xs">Agência inclusiva e acolhedora</span>
          <span className="text-lg" role="img" aria-label="LGBTQ+ friendly">🏳️‍🌈</span>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
